import { Application, type TickerCallback } from 'pixi.js';
import { GameStateManager } from './states/GameStateManager';
import { TitleState } from './states/TitleState';
import { LabyrinthState } from './states/LabyrinthState';
import { initViewport } from './rendering/Viewport';

const app = new Application();

// Module-level refs so HMR can tear everything down before re-init.
let tickerCb: TickerCallback<unknown> | null = null;
let visibilityCb: (() => void) | null = null;
let bgIntervalId: number | null = null;
let initialized = false;

async function init() {
  if (initialized) return;
  initialized = true;

  await app.init({
    background: '#0a0a0a',
    backgroundAlpha: 0,
    resizeTo: window,
    antialias: true,
  });

  document.body.appendChild(app.canvas);
  // Ensure Pixi canvas stacks above the Three.js canvas (z-index 0, position:fixed)
  // so HUD / UI overlays the 3D world.
  app.canvas.style.position = 'fixed';
  app.canvas.style.inset = '0';
  app.canvas.style.zIndex = '1';

  initViewport(app);

  await Promise.all([
    document.fonts.load('16px Gugi'),
    document.fonts.load('16px Offside'),
  ]);

  const stateManager = new GameStateManager(app);
  stateManager.switchTo(new TitleState(stateManager));

  tickerCb = (ticker) => {
    stateManager.update(ticker.deltaMS / 1000);
  };
  app.ticker.add(tickerCb);

  // Background-tab simulation: RAF (and thus Pixi's ticker) is paused when
  // the tab/window is hidden. To let Labyrinth exploration keep progressing,
  // swap in a setInterval-driven update while hidden. Browsers clamp
  // background intervals to ~1s, so we sub-step the dt to keep sim stable.
  const MAX_SUBSTEP = 0.1;
  const MAX_CATCHUP = 5;
  let bgLastMs = 0;

  const stepBackground = () => {
    const now = performance.now();
    let elapsed = (now - bgLastMs) / 1000;
    bgLastMs = now;
    if (elapsed > MAX_CATCHUP) elapsed = MAX_CATCHUP;
    while (elapsed > 0) {
      const step = Math.min(elapsed, MAX_SUBSTEP);
      stateManager.update(step);
      elapsed -= step;
    }
  };

  const shouldRunInBackground = () => stateManager.state instanceof LabyrinthState;

  visibilityCb = () => {
    if (document.hidden) {
      if (shouldRunInBackground() && bgIntervalId === null) {
        app.ticker.stop();
        bgLastMs = performance.now();
        bgIntervalId = window.setInterval(stepBackground, 250);
      }
      stateManager.sound.setPaused(true);
    } else {
      if (bgIntervalId !== null) {
        window.clearInterval(bgIntervalId);
        bgIntervalId = null;
        app.ticker.start();
      }
      stateManager.sound.setPaused(false);
    }
  };
  document.addEventListener('visibilitychange', visibilityCb);
}

// Vite HMR: tear down all global hooks and the Pixi app when this module is
// replaced. Without this, dev reloads stack duplicate ticker callbacks,
// visibilitychange listeners, and background intervals.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (visibilityCb) {
      document.removeEventListener('visibilitychange', visibilityCb);
      visibilityCb = null;
    }
    if (bgIntervalId !== null) {
      window.clearInterval(bgIntervalId);
      bgIntervalId = null;
    }
    if (tickerCb) {
      app.ticker.remove(tickerCb);
      tickerCb = null;
    }
    if (app.canvas && app.canvas.parentNode) {
      app.canvas.parentNode.removeChild(app.canvas);
    }
    app.destroy(true, { children: true, texture: true });
    initialized = false;
  });
}

void init();
